import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Paper, 
  Typography, 
  Avatar,
  ThemeProvider,
  createTheme,
  alpha,
  Button
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

interface Agent {
  name: string;
  avatar: string;
}

interface Message {
  text: string;
  sender: 'user' | 'bot';
  timestamp: string;
  agentName?: string;
  agentAvatar?: string;
}

interface ThemeConfig {
  primary: string;
  secondary: string;
  text: string;
  background: string;
  userMessageBg: string;
  userMessageText: string;
  botMessageBg: string;
  botMessageText: string;
  headerBg: string;
  headerText: string;
  inputBg: string;
  inputText: string;
  borderRadius: string;
  fontFamily: string;
}

interface LayoutConfig {
  showHeader: boolean;
  showMinimize: boolean;
  showClose: boolean;
  showAttachButton: boolean;
  headerTitle: string;
  headerSubtitle: string;
  avatarSize: number;
  messageMaxWidth: string;
  messageSpacing: number;
  inputHeight: number;
  labels?: {
    welcomeTitle?: string;
    welcomeNameLabel?: string;
    welcomeEmailLabel?: string;
    welcomeButton?: string;
    termsTitle?: string;
    termsText?: string;
    termsButton?: string;
  };
  fontSizes?: {
    welcomeTitle?: string;
    input?: string;
    termsTitle?: string;
    termsText?: string;
  };
}

interface BehaviorConfig {
  autoScroll: boolean;
  showTimestamps: boolean;
  showAgentName: boolean;
  showTypingIndicator: boolean;
  allowFileUpload: boolean;
  maxFileSize: number;
  allowedFileTypes: string[];
}

export interface N8nChatProps {
  webhookUrl: string;
  onMessageReceived?: (message: Message) => void;
  initialMessages?: Message[];
  placeholder?: string;
  height?: string;
  width?: string;
  agentName?: string;
  agentAvatar?: string;
  theme?: Partial<ThemeConfig>;
  layout?: Partial<LayoutConfig>;
  behavior?: Partial<BehaviorConfig>;
  sessionId?: string;
}

const AGENTS: Agent[] = [
  { name: 'John', avatar: 'https://i.pravatar.cc/150?img=1' },
  { name: 'Michael', avatar: 'https://i.pravatar.cc/150?img=2' },
  { name: 'Sarah', avatar: 'https://i.pravatar.cc/150?img=3' },
  { name: 'Emma', avatar: 'https://i.pravatar.cc/150?img=4' },
  { name: 'David', avatar: 'https://i.pravatar.cc/150?img=5' }
];

const defaultTheme: ThemeConfig = {
  primary: '#0084ff',
  secondary: '#e5e5ea',
  text: '#222222',
  background: '#f0f2f5',
  userMessageBg: '#0084ff',
  userMessageText: '#fff',
  botMessageBg: '#e5e5ea',
  botMessageText: '#222222',
  headerBg: '#0084ff',
  headerText: '#fff',
  inputBg: '#fff',
  inputText: '#222222',
  borderRadius: '22px',
  fontFamily: 'Segoe UI, Nunito, Roboto, Arial, sans-serif'
};

const defaultLayout: LayoutConfig = {
  showHeader: true,
  showMinimize: true,
  showClose: true,
  showAttachButton: true,
  headerTitle: 'Chat',
  headerSubtitle: 'Ask me anything!',
  avatarSize: 40,
  messageMaxWidth: '80%',
  messageSpacing: 6,
  inputHeight: 52,
  labels: {
    welcomeTitle: "Welcome! Please introduce yourself",
    welcomeNameLabel: "Name",
    welcomeEmailLabel: "Email",
    welcomeButton: "Start Chat",
    termsTitle: "Terms and Conditions",
    termsText: "By using this chat, you agree to our Terms and Conditions. Your messages may be processed and stored for quality and support purposes. Please do not share sensitive personal information. For more details, see our Privacy Policy.",
    termsButton: "I Agree"
  },
  fontSizes: {
    welcomeTitle: '1.25rem',
    input: '1rem',
    termsTitle: '1.1rem',
    termsText: '0.95rem'
  },
};

const defaultBehavior: BehaviorConfig = {
  autoScroll: true,
  showTimestamps: true,
  showAgentName: true,
  showTypingIndicator: true,
  allowFileUpload: false,
  maxFileSize: 5,
  allowedFileTypes: ['image/jpeg', 'image/png', 'application/pdf']
};

// Session storage keys (same as FloatingChatWidget)
const STORAGE_KEYS = {
  SESSION_ID: 'n8n_chat_session_id',
  USER_NAME: 'n8n_chat_user_name',
  USER_EMAIL: 'n8n_chat_user_email',
  TERMS_ACCEPTED: 'n8n_chat_terms_accepted',
  CHAT_HISTORY: 'n8n_chat_history',
  SESSION_TIMESTAMP: 'n8n_chat_session_timestamp'
};

// Helper functions for localStorage
const storage = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return null;
    }
  },
  
  set: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
    }
  }
};

// Helper to extract message from various response formats
function extractMessage(responseData: any): string {
  console.log('Raw response data:', responseData);
  
  let message = '';
  
  // Case 1: Array with output.message
  if (Array.isArray(responseData) && responseData[0]?.output?.message) {
    message = responseData[0].output.message;
  }
  // Case 2: Object with output.message
  else if (responseData?.output?.message) {
    message = responseData.output.message;
  }
  // Case 3: Object with message
  else if (responseData?.message) {
    message = responseData.message;
  }
  // Case 4: Just a string
  else if (typeof responseData === 'string') {
    message = responseData;
  }
  
  console.log('Extracted message before fixing:', message);
  
  // Fix double-escaped HTML
  const fixedMessage = fixDoubleEscapedHTML(message);
  
  console.log('Fixed message:', fixedMessage);
  
  return fixedMessage;
}

// Helper to fix double-escaped HTML characters
function fixDoubleEscapedHTML(html: string): string {
  if (!html || typeof html !== 'string') return html;
  
  // Fix double-escaped quotes: \\\" -> \"
  let fixed = html.replace(/\\\"/g, '"');
  
  // Fix double-escaped backslashes: \\\\ -> \\
  fixed = fixed.replace(/\\\\/g, '\\');
  
  // Fix other common double-escaped characters
  fixed = fixed.replace(/\\n/g, '\n');
  fixed = fixed.replace(/\\t/g, '\t');
  fixed = fixed.replace(/\\r/g, '\r');
  
  return fixed;
}

const N8nChat: React.FC<N8nChatProps> = ({ 
  webhookUrl, 
  onMessageReceived,
  initialMessages = [],
  placeholder = "Type your message...",
  height = "400px",
  width = "100%",
  agentName,
  agentAvatar,
  theme: themeOverrides = {},
  layout: layoutOverrides = {},
  behavior: behaviorOverrides = {},
  sessionId: propSessionId
}) => {
  // Load persisted data or use defaults
  const [messages, setMessages] = useState<Message[]>(() => {
    const savedMessages = storage.get(STORAGE_KEYS.CHAT_HISTORY);
    if (savedMessages) {
      try {
        return JSON.parse(savedMessages);
      } catch (error) {
        console.warn('Failed to parse saved messages:', error);
      }
    }
    return initialMessages;
  });
  
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => propSessionId || uuidv4());
  
  // Load persisted user data
  const [userName, setUserName] = useState(() => 
    storage.get(STORAGE_KEYS.USER_NAME) || ''
  );
  const [userEmail, setUserEmail] = useState(() => 
    storage.get(STORAGE_KEYS.USER_EMAIL) || ''
  );
  const [preChatComplete, setPreChatComplete] = useState(() => {
    const savedName = storage.get(STORAGE_KEYS.USER_NAME);
    const savedEmail = storage.get(STORAGE_KEYS.USER_EMAIL);
    const termsAccepted = storage.get(STORAGE_KEYS.TERMS_ACCEPTED);
    return !!(savedName && savedEmail && termsAccepted);
  });
  
  const [preChatError, setPreChatError] = useState('');
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(() => 
    storage.get(STORAGE_KEYS.TERMS_ACCEPTED) === 'true'
  );
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const theme = { ...defaultTheme, ...themeOverrides };
  const layout = { ...defaultLayout, ...layoutOverrides };
  const behavior = { ...defaultBehavior, ...behaviorOverrides };
  
  // Create theme based on provided configuration
  const chatTheme = createTheme({
    palette: {
      primary: {
        main: theme.primary,
      },
      background: {
        default: theme.background,
        paper: theme.botMessageBg,
      },
      text: {
        primary: theme.text,
      },
    },
    typography: {
      fontFamily: theme.fontFamily,
    },
    shape: {
      borderRadius: parseInt(theme.borderRadius),
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: theme.borderRadius,
          },
        },
      },
    },
  });

  // Select a random agent if not provided
  const [currentAgent] = useState<Agent>(() => {
    if (agentName && agentAvatar) {
      return { name: agentName, avatar: agentAvatar };
    }
    return AGENTS[Math.floor(Math.random() * AGENTS.length)];
  });

  const scrollToBottom = () => {
    if (behavior.autoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      storage.set(STORAGE_KEYS.CHAT_HISTORY, JSON.stringify(messages));
    }
  }, [messages]);

  // Save user data to localStorage when it changes
  useEffect(() => {
    if (userName) {
      storage.set(STORAGE_KEYS.USER_NAME, userName);
    }
  }, [userName]);

  useEffect(() => {
    if (userEmail) {
      storage.set(STORAGE_KEYS.USER_EMAIL, userEmail);
    }
  }, [userEmail]);

  useEffect(() => {
    storage.set(STORAGE_KEYS.TERMS_ACCEPTED, hasAgreedToTerms.toString());
  }, [hasAgreedToTerms]);

  // Pre-chat form validation
  const handlePreChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userName.trim() || !userEmail.trim()) {
      setPreChatError('Please enter your name and email.');
      return;
    }
    // Simple email validation
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(userEmail)) {
      setPreChatError('Please enter a valid email address.');
      return;
    }
    setPreChatError('');
    setPreChatComplete(true);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const newMessage: Message = {
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      console.log('Sending message to webhook:', webhookUrl);
      const response = await axios.post(webhookUrl, {
        sessionId: sessionId,
        chatInput: inputMessage,
        userName: userName,
        userEmail: userEmail
      });
      console.log('Webhook response:', response.data);

      // Use the helper to extract the message
      const botReply = extractMessage(response.data);

      if (!botReply) {
        throw new Error('Invalid response format from webhook');
      }

      const botMessage: Message = {
        text: botReply,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        agentName: currentAgent.name,
        agentAvatar: currentAgent.avatar
      };

      setMessages(prev => [...prev, botMessage]);
      onMessageReceived?.(botMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMessage = 'Sorry, there was an error processing your message.';
      if (axios.isAxiosError(error)) {
        if (error.response) {
          errorMessage = `Error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
        } else if (error.request) {
          errorMessage = 'No response received from server. Please check your webhook URL.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      const errorBotMessage: Message = {
        text: errorMessage,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorBotMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > behavior.maxFileSize * 1024 * 1024) {
      alert(`File size must be less than ${behavior.maxFileSize}MB`);
      return;
    }

    if (!behavior.allowedFileTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.replace('/*', ''));
      }
      return file.type === type;
    })) {
      alert('File type not allowed');
      return;
    }

    // Handle file upload logic here
    console.log('File uploaded:', file);
  };

  // Send T&C agreement to webhook when user agrees
  const handleAgreeToTerms = async () => {
    setHasAgreedToTerms(true);
    try {
      await axios.post(webhookUrl, {
        sessionId: sessionId,
        userName: userName,
        userEmail: userEmail,
        agreedToTerms: true,
        event: 'start'
      });
    } catch (err) {
      // Fail silently, do not block UI
      console.error('Failed to notify webhook of T&C agreement:', err);
    }
  };

  const labels = {
    welcomeTitle: layout.labels?.welcomeTitle || "Welcome! Please introduce yourself",
    welcomeNameLabel: layout.labels?.welcomeNameLabel || "Name",
    welcomeEmailLabel: layout.labels?.welcomeEmailLabel || "Email",
    welcomeButton: layout.labels?.welcomeButton || "Start Chat",
    termsTitle: layout.labels?.termsTitle || "Terms and Conditions",
    termsText: layout.labels?.termsText || "By using this chat, you agree to our Terms and Conditions. Your messages may be processed and stored for quality and support purposes. Please do not share sensitive personal information. For more details, see our Privacy Policy.",
    termsButton: layout.labels?.termsButton || "I Agree"
  };
  const fontSizes = {
    welcomeTitle: layout.fontSizes?.welcomeTitle || '1.25rem',
    input: layout.fontSizes?.input || '1rem',
    termsTitle: layout.fontSizes?.termsTitle || '1.1rem',
    termsText: layout.fontSizes?.termsText || '0.95rem'
  };

  return (
    <ThemeProvider theme={chatTheme}>
      <Box
        sx={{
          height,
          width,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.background,
          borderRadius: theme.borderRadius,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
        }}
      >
        {layout.showHeader && (
          <Box
            sx={{
              bgcolor: theme.headerBg,
              color: theme.headerText,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: theme.fontFamily,
              fontSize: '1.2rem',
              fontWeight: 600
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={currentAgent.avatar}
                alt={currentAgent.name}
                sx={{ width: layout.avatarSize, height: layout.avatarSize, boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: theme.fontFamily }}>{layout.headerTitle}</Typography>
                <Typography variant="caption" sx={{ fontFamily: theme.fontFamily }}>{layout.headerSubtitle}</Typography>
              </Box>
            </Box>
            <Box>
              {layout.showMinimize && (
                <IconButton
                  size="small"
                  onClick={() => setIsMinimized(!isMinimized)}
                  sx={{ color: theme.headerText }}
                >
                  <MinimizeIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        )}

        {!isMinimized && !preChatComplete && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, background: theme.background }}>
            <Paper sx={{ p: 4, borderRadius: theme.borderRadius, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minWidth: 320 }}>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: theme.fontFamily, textAlign: 'center', fontSize: fontSizes.welcomeTitle }}>{labels.welcomeTitle}</Typography>
              <form onSubmit={handlePreChatSubmit}>
                <TextField
                  label={labels.welcomeNameLabel}
                  value={userName}
                  onChange={e => setUserName(e.target.value)}
                  fullWidth
                  sx={{ mb: 2, fontSize: fontSizes.input }}
                  autoFocus
                />
                <TextField
                  label={labels.welcomeEmailLabel}
                  value={userEmail}
                  onChange={e => setUserEmail(e.target.value)}
                  fullWidth
                  sx={{
                    mb: 2,
                    fontSize: fontSizes.input,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: theme.inputBg,
                      color: theme.inputText,
                      height: layout.inputHeight,
                      borderRadius: theme.borderRadius,
                      fontFamily: theme.fontFamily,
                      fontSize: fontSizes.input,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      border: '1px solid #ccc',
                      '& fieldset': {
                        border: '1px solid #ccc !important',
                        boxShadow: 'none !important',
                      },
                      '&:hover fieldset': {
                        border: '1.5px solid #1976d2 !important',
                      },
                      '&.Mui-focused fieldset': {
                        border: `2px solid ${theme.primary} !important`,
                      },
                    },
                    '& input': {
                      background: theme.inputBg,
                      color: theme.inputText,
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none',
                    }
                  }}
                />
                {preChatError && <Typography color="error" sx={{ mb: 2 }}>{preChatError}</Typography>}
                <Button type="submit" variant="contained" color="primary" fullWidth>{labels.welcomeButton}</Button>
              </form>
            </Paper>
          </Box>
        )}

        {!isMinimized && preChatComplete && !hasAgreedToTerms && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, background: theme.background }}>
            <Paper sx={{ p: 4, borderRadius: theme.borderRadius, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minWidth: 320, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: theme.fontFamily, fontSize: fontSizes.termsTitle }}>{labels.termsTitle}</Typography>
              <Box sx={{ mb: 2, maxHeight: 180, overflowY: 'auto', textAlign: 'left', fontSize: fontSizes.termsText }}>
                <Typography variant="body2">{labels.termsText}</Typography>
              </Box>
              <Button variant="contained" color="primary" onClick={handleAgreeToTerms} fullWidth>{labels.termsButton}</Button>
            </Paper>
          </Box>
        )}

        {!isMinimized && preChatComplete && hasAgreedToTerms && (
          <>
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: layout.messageSpacing,
                fontFamily: theme.fontFamily,
                fontSize: '0.825rem',
                background: theme.background
              }}
            >
              {messages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    gap: 0.5
                  }}
                >
                  {message.sender === 'bot' && behavior.showAgentName && (
                    <Avatar
                      src={message.agentAvatar || currentAgent.avatar}
                      alt={message.agentName || currentAgent.name}
                      sx={{ width: layout.avatarSize, height: layout.avatarSize, boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
                    />
                  )}
                  <Paper
                    sx={{
                      maxWidth: layout.messageMaxWidth,
                      p: 1.5,
                      bgcolor: message.sender === 'user' ? theme.userMessageBg : theme.botMessageBg,
                      color: message.sender === 'user' ? theme.userMessageText : theme.botMessageText,
                      borderRadius: message.sender === 'user'
                        ? `${theme.borderRadius} ${theme.borderRadius} 6px ${theme.borderRadius}`
                        : `${theme.borderRadius} 6px ${theme.borderRadius} ${theme.borderRadius}`,
                      fontFamily: theme.fontFamily,
                      fontSize: '0.825rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      mb: 0,
                      textAlign: 'left',
                      '& p, & ul, & ol, & li': {
                        marginTop: 0,
                        marginBottom: '0.25em', // 50% less than default
                        padding: 0,
                      },
                    }}
                  >
                    {behavior.showAgentName && message.sender === 'bot' && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontFamily: theme.fontFamily, fontWeight: 600, textAlign: 'left', fontSize: '0.7rem' }}>
                        {message.agentName || currentAgent.name}
                      </Typography>
                    )}
                    <Typography
                      sx={{ fontFamily: theme.fontFamily, fontSize: '0.825rem', textAlign: 'left' }}
                      component="span"
                      dangerouslySetInnerHTML={{ __html: message.text }}
                    />
                    {behavior.showTimestamps && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, fontFamily: theme.fontFamily, textAlign: 'left', fontSize: '0.65rem' }}>
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </Paper>
                  {/* Only show avatar for user if you want a generic one, otherwise skip */}
                  {/* {message.sender === 'user' && (
                    <Avatar
                      src={currentAgent.avatar}
                      alt="You"
                      sx={{ width: layout.avatarSize, height: layout.avatarSize, boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
                    />
                  )} */}
                </Box>
              ))}
              {isTyping && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                    gap: 0.5
                  }}
                >
                  <Avatar
                    src={currentAgent.avatar}
                    alt={currentAgent.name}
                    sx={{ width: layout.avatarSize, height: layout.avatarSize, boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
                  />
                  <Paper
                    sx={{
                      p: 1.5,
                      bgcolor: theme.botMessageBg,
                      color: theme.botMessageText,
                      borderRadius: `${theme.borderRadius} 6px ${theme.borderRadius} ${theme.borderRadius}`,
                      fontFamily: theme.fontFamily,
                      fontSize: '0.825rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                  >
                    <Typography sx={{ fontFamily: theme.fontFamily, fontSize: '0.825rem', textAlign: 'left' }}>Typing...</Typography>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${alpha(theme.text, 0.1)}`,
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                background: theme.background
              }}
            >
              {layout.showAttachButton && behavior.allowFileUpload && (
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ color: theme.primary }}
                >
                  <AttachFileIcon />
                </IconButton>
              )}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept={behavior.allowedFileTypes.join(',')}
              />
              <TextField
                fullWidth
                variant="outlined"
                placeholder={placeholder}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme.inputBg,
                    color: theme.inputText,
                    height: layout.inputHeight,
                    borderRadius: theme.borderRadius,
                    fontFamily: theme.fontFamily,
                    fontSize: '0.825rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                sx={{
                  bgcolor: theme.primary,
                  color: '#fff',
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  ml: 1,
                  '&:hover': {
                    bgcolor: '#005ecb'
                  }
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 1,
                borderTop: `1px solid ${alpha(theme.text, 0.05)}`,
                background: theme.background,
                textAlign: 'center'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: theme.fontFamily,
                  fontSize: '0.7rem',
                  color: alpha(theme.text, 0.6),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5
                }}
              >
                Powered by{' '}
                <a
                  href="https://www.akhtarsitsolutions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.primary,
                    textDecoration: 'none',
                    fontWeight: 600
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  AKITS
                </a>
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default N8nChat; 